import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskLine, Rise } from "@/components/motion/Reveal";
import { CYBER_SPOTLIGHT, TEAM } from "@/data/site";
import { useLang } from "@/i18n/LangContext";

export function CyberSpotlight() {
  const { t } = useLang();
  const s = t.cyberSpotlight;
  const lead = TEAM[1];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section ref={ref} data-testid="home-cyber-spotlight" className="relative border-b border-[#141414] bg-[#0A0A0A] overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12">
        <div className="lg:col-span-5 relative min-h-[60vh] lg:min-h-0 overflow-hidden">
          <motion.img
            style={{ y }}
            src={CYBER_SPOTLIGHT.img}
            alt="Downtown Dubai skyline at night"
            className="absolute inset-0 w-full h-[120%] -top-[10%] object-cover photo-mono"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-[#0A0A0A]" />
          <div className="absolute top-8 left-6 lg:left-12 flex items-center gap-4">
            <span className="accent-bar" />
            <span className="chapter-num text-[#F4F1EB]" data-testid="cyber-spotlight-tag">{s.tag}</span>
          </div>
        </div>

        <div className="lg:col-span-7 px-6 lg:px-16 py-24 lg:py-32">
          <Rise><div className="flex items-center gap-4 mb-10"><span className="accent-bar" /><span className="eyebrow text-[#C84C35]">{s.eyebrow}</span></div></Rise>
          <h2 className="font-serif tracking-tight leading-[0.92] text-5xl lg:text-[5.4vw]">
            <MaskLine delay={0.1}>{s.title1}</MaskLine>
            <MaskLine delay={0.25}><span className="italic-accent">{s.title2}</span></MaskLine>
            <MaskLine delay={0.4}>{s.title3}</MaskLine>
          </h2>
          <Rise delay={0.5}><p className="mt-10 text-[#A19D94] text-lg leading-relaxed max-w-2xl">{s.lead}</p></Rise>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#141414] border border-[#141414]">
            {CYBER_SPOTLIGHT.services.map((c, i) => (
              <Rise key={c.n} delay={0.05 * i} className="bg-[#0A0A0A]">
                <div className="p-7 lg:p-8 h-full" data-testid={`cyber-service-${c.n}`}>
                  <div className="chapter-num text-[#C84C35]">§{c.n}</div>
                  <div className="font-serif text-2xl lg:text-[1.75rem] mt-4 leading-tight">{c.t}</div>
                  <p className="mt-3 text-[#A19D94] text-sm leading-relaxed">{c.d}</p>
                </div>
              </Rise>
            ))}
          </div>

          <Rise delay={0.2}>
            <div className="mt-14 flex flex-col md:flex-row md:items-center justify-between gap-8 border-t border-[#141414] pt-10">
              <Link to="/team/rajas-pingle" data-testid="cyber-spotlight-lead" className="flex items-center gap-5 group">
                <img src={lead.img} alt={lead.name} className="w-16 h-16 object-cover object-top photo-mono" />
                <div>
                  <div className="chapter-num text-[#66635C]">{s.lead_label}</div>
                  <div className="font-serif text-2xl mt-1 group-hover:text-[#C84C35] transition-colors duration-500">{lead.name}</div>
                  <div className="text-[#A19D94] text-xs mt-1 link-hover">{s.profile}</div>
                </div>
              </Link>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact" data-testid="cyber-spotlight-cta" className="btn-primary">{s.cta} <ArrowUpRight size={14} strokeWidth={1.5} /></Link>
                <Link to="/practice-areas/cyber-crime-lawyer-dubai" data-testid="cyber-spotlight-explore" className="btn-ghost">{t.practiceDetail.explore}</Link>
              </div>
            </div>
          </Rise>
        </div>
      </div>
    </section>
  );
}
